import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useAppSelector } from '../../app/hooks';
import useHypothesisFrames from '../../hooks/useHypothesisFrames.hooks';
import useAgentCartSync from '../../hooks/useAgentCartSync.hooks';
import useExpressTimer from '../../hooks/useExpressTimer.hooks';
import useUnresolvedItemsSummary from '../../hooks/useUnresolvedItemsSummary.hooks';
import useSessionStatus from '../../hooks/sessionStatus/useSessionStatus.hooks';
import { ICartItemRefs, SetCartItemRef } from '../../types';
import Colors from '../../utils/color';
import { selectFeatureFlagFinalOrderValidation } from '../../redux/features/config/config.selector';
import FinalOrderBackdrop from '../FinalOrderBackdrop';
import LayoutPreferenceDialog, {
  DEFAULT_PANE_SIZES,
  MAX_PANE_SIZE,
  MIN_PANE_SIZE,
} from '../Layout/LayoutPreferenceDialog';
import BottomActionBar from './BottomActionBar';
import CenterPane from './CenterPane';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import useAgentResponseTimeMetric from './hooks/useAgentResponseTimeMetric.hooks';
import useMenuTimePeriodRollover from './hooks/useMenuTimePeriodRollover.hooks';
import usePaneLayout from './hooks/usePaneLayout.hooks';
import useRightPaneWidth from './hooks/useRightPaneWidth.hooks';
import useTranscriptExpand from './useTranscriptExpand.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  page: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.porcelain,
  },
  topRow: {
    flex: 1,
    minHeight: 0,
    // Horizontal padding keeps the panes off the screen edges; the PanelGroup
    // fills this box. Positioning context for the Live Transcript takeover.
    padding: `0 ${spacing(1)}`,
    position: 'relative',
  },
  // Draggable divider between panes — occupies the old inter-pane gap and shows
  // a grabber line that highlights on hover.
  resizeHandle: {
    width: spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    '&::after': {
      content: '""',
      width: 3,
      height: spacing(5),
      borderRadius: 2,
      // Idle grip needs to read against the grey card backdrop — Colors.alto
      // was too faint; silverChalice is clearly visible.
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
    },
  },
  rightPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    paddingTop: spacing(1),
  },
}));

/**
 * MainPageLayout — new HITL terminal layout (replacement for MainContent).
 *
 * The three panes are resizable (react-resizable-panels); sizes persist per
 * agent via `autoSaveId`, and the "Layout preference" dialog drives them
 * imperatively. The top app bar (Header) lives outside this component (App.tsx).
 *
 * This component is the composition root only: the three panes are LeftSidebar /
 * CenterPane / the Agent Cart, and the stateful wiring (pane persistence, the
 * takeover-overlay width measurement, day-part menu rollover) lives in the
 * co-located hooks under ./hooks.
 */
const MainPageLayout = () => {
  const classes = useStyles();
  const cartItemRefs = useRef<ICartItemRefs>({});
  const setCartItemRef: SetCartItemRef = (cartItemId, ref) => {
    cartItemRefs.current[cartItemId] = ref;
  };

  // Left-card takeover expansion (transcript + reminder + AI status grow as one).
  const { isExpanded, setIsExpanded } = useTranscriptExpand();

  // Resizable-pane wiring + layout-preference dialog state.
  const {
    paneStorageKey,
    panelGroupRef,
    liveSizes,
    setLiveSizes,
    isLayoutPrefOpen,
    closeLayoutPref,
    applyPaneSizes,
    resetPaneSizes,
  } = usePaneLayout();

  // The takeover overlay stops before the (resizable) right cart pane, so track
  // its live pixel width and offset the overlay from it.
  const { rightPaneRef, rightPaneWidth } = useRightPaneWidth();

  const FEATURE_FLAG_FINAL_ORDER_VALIDATION = useAppSelector(
    selectFeatureFlagFinalOrderValidation
  );

  // Poll the clock to roll the menu over between day-parts (breakfast/lunch/…).
  useMenuTimePeriodRollover();

  // Core call-handling side effects (cart rebuild from hypotheses, unresolved
  // items summary, session lifecycle). These run for their effects only.
  useHypothesisFrames();
  useUnresolvedItemsSummary();
  useSessionStatus();
  useAgentCartSync();
  useExpressTimer();
  // Emits agent_metric (agent_response_time) when a turn's response time exceeds
  // the configured limit during a takeover. Mounted here so it fires regardless
  // of the Pulse card's collapse state.
  useAgentResponseTimeMetric();

  return (
    <div id="main-page-layout" className={classes.page}>
      <div className={classes.topRow}>
        <PanelGroup
          direction="horizontal"
          autoSaveId={paneStorageKey}
          ref={panelGroupRef}
          onLayout={setLiveSizes}
        >
          {/* LEFT SIDEBAR: one card — transcript + reminder sections + AI status.
              overflow:visible while expanded lets the takeover overlay escape. */}
          <Panel
            id="left"
            order={1}
            defaultSize={DEFAULT_PANE_SIZES[0]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
            style={{ overflow: isExpanded ? 'visible' : 'hidden' }}
          >
            <LeftSidebar
              paneStorageKey={paneStorageKey}
              isExpanded={isExpanded}
              onCollapseTranscript={() => setIsExpanded(false)}
              rightPaneWidth={rightPaneWidth}
            />
          </Panel>

          <PanelResizeHandle className={classes.resizeHandle} />

          {/* MIDDLE: one card — current step (top) + option groups (ItemBuilder) */}
          <Panel
            id="middle"
            order={2}
            defaultSize={DEFAULT_PANE_SIZES[1]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
          >
            <CenterPane setCartItemRef={setCartItemRef} />
          </Panel>

          <PanelResizeHandle className={classes.resizeHandle} />

          {/* RIGHT: agent cart + totals + order actions */}
          <Panel
            id="right"
            order={3}
            defaultSize={DEFAULT_PANE_SIZES[2]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
          >
            <div className={classes.rightPane} ref={rightPaneRef}>
              <RightSidebar paneStorageKey={paneStorageKey} />
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {/* BOTTOM: quick response + conversation + order flow + escalation */}
      <BottomActionBar />

      <LayoutPreferenceDialog
        open={isLayoutPrefOpen}
        onClose={closeLayoutPref}
        sizes={liveSizes}
        onApply={applyPaneSizes}
        onReset={resetPaneSizes}
      />

      {FEATURE_FLAG_FINAL_ORDER_VALIDATION && <FinalOrderBackdrop />}
    </div>
  );
};

export default MainPageLayout;
